/**
 * Continuous scrolling ticker for the announcement bar (sections/header-announcements.liquid,
 * "Scroll as a ticker" on).
 *
 * Takes the announcements this visitor can see (already filtered by country in Liquid),
 * strips their own ★ decorations so there's exactly one star between messages, repeats them end to end until they fill the bar, then doubles that strip so it can loop
 * seamlessly: the track scrolls left by exactly half its width and starts again.
 * The scroll speed is constant in pixels per second, whatever the screen size.
 */

const PIXELS_PER_SECOND = 25;

class AnnouncementTicker extends HTMLElement {
  #resizeObserver = null;
  #lastWidth = 0;

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#build();
    this.#resizeObserver = new ResizeObserver(() => {
      if (Math.abs(this.clientWidth - this.#lastWidth) > 1) this.#build();
    });
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
  }

  #build() {
    const sources = Array.from(this.querySelectorAll(':scope > .announcement-bar__slide'));
    if (sources.length === 0) return;

    this.#lastWidth = this.clientWidth;
    this.querySelector(':scope > .announcement-ticker__track')?.remove();

    const track = document.createElement('div');
    track.className = 'announcement-ticker__track';
    track.setAttribute('aria-hidden', 'true');

    const group = document.createElement('div');
    group.className = 'announcement-ticker__group';
    track.append(group);
    this.append(track);
    this.setAttribute('data-ready', '');

    // Keep adding the visitor's messages, in order, until one group is at least as wide as the bar.
    const minimumWidth = Math.max(this.clientWidth, 320);
    let guard = 0;
    do {
      for (const source of sources) group.append(this.#itemFrom(source));
      guard += 1;
    } while (group.scrollWidth < minimumWidth && guard < 50);

    // Second identical group so the loop is seamless.
    track.append(group.cloneNode(true));

    const duration = group.scrollWidth / PIXELS_PER_SECOND;
    track.style.setProperty('--ticker-duration', `${duration}s`);
  }

  /**
   * A copy of one announcement's text, keeping the slide's typography classes and styles.
   * @param {Element} source
   */
  #itemFrom(source) {
    const item = document.createElement('div');
    item.className = source.className.replace('announcement-bar__slide', '').trim();
    item.classList.add('announcement-ticker__item');
    const style = source.getAttribute('style');
    if (style) item.setAttribute('style', style);
    // Slides are full-width; ticker items only take up as much room as their text.
    item.style.setProperty('--width', 'max-content');

    const text = source.querySelector('.announcement-bar__text');
    if (text) {
      const copy = text.cloneNode(false);
      copy.textContent = text.textContent.replace(/^[\s★]+|[\s★]+$/g, '');
      const separator = document.createElement('span');
      separator.className = 'announcement-ticker__separator';
      separator.textContent = '★';
      copy.append(separator);
      item.append(copy);
    }
    return item;
  }
}

if (!customElements.get('announcement-ticker')) {
  customElements.define('announcement-ticker', AnnouncementTicker);
}
